import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { MessageSquare, Send, Lock } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { getCurrentUser } from '../../utils/auth';
import UserAvatar from '../ui/UserAvatar';
import { getCommentAuthorName, getCommentAuthorRole, getDisplayName } from './supportIdentity';
import './Support.scss';

const formatTime = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const TicketComments = ({ ticketId, comments = [], onCommentAdded, allowInternal = false }) => {
  const [text, setText] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [posting, setPosting] = useState(false);
  const user = getCurrentUser();

  const handlePost = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    try {
      const { data } = await apiClient.post(`/support/tickets/${ticketId}/comment`, {
        text: text.trim(),
        isInternal,
      });
      onCommentAdded?.(data.comment || {
        text: text.trim(),
        createdAt: new Date(),
        userName: getDisplayName(user, 'Support'),
        userRole: user?.usertype,
        isInternal,
      });
      setText('');
      setIsInternal(false);
    } catch {
      // handled by apiClient
    } finally {
      setPosting(false);
    }
  };

  // Filter out internal comments for non-admin users
  const visibleComments = allowInternal ? comments : comments.filter((c) => !c.isInternal);
  const publicCount = comments.filter((c) => !c.isInternal).length;
  const sectionTitle = allowInternal
    ? `Activity (${publicCount} ${publicCount === 1 ? 'reply' : 'replies'})`
    : `Responses (${visibleComments.length})`;

  return (
    <div className="tc-section">
      <div className="tc-section__header">
        <MessageSquare size={16} />
        <span>{sectionTitle}</span>
      </div>

      <div className="tc-section__body">
        {visibleComments.length === 0 ? (
          <div className="tc-empty">
            <MessageSquare size={32} strokeWidth={1.2} />
            <p>{allowInternal ? 'No responses yet' : 'No responses yet. Our team will get back to you soon.'}</p>
          </div>
        ) : (
          visibleComments.map((c, i) => {
            const authorName = getCommentAuthorName(c);
            const roleLabel = getCommentAuthorRole(c);
            const roleValue = roleLabel.toLowerCase();
            const isAdmin = ['admin', 'superadmin', 'tenantadmin', 'instructor'].includes(roleValue);
            return (
              <div key={c._id || i} className={`tc-item${c.isInternal ? ' tc-item--internal' : ''}`}>
                <UserAvatar name={authorName} size="sm" />
                <div className="tc-item__body">
                  <div className="tc-item__header">
                    <span className={`tc-item__name${isAdmin ? ' tc-item__name--admin' : ''}`}>{authorName}</span>
                    {roleLabel && <span className="tc-item__role-badge">{roleLabel}</span>}
                    {c.isInternal && (
                      <span className="tc-item__internal-badge">
                        <Lock size={10} /> Internal
                      </span>
                    )}
                    <span className="tc-item__time">{formatTime(c.createdAt)}</span>
                  </div>
                  <p className="tc-item__text">{c.text}</p>
                </div>
              </div>
            );
          })
        )}

        <div className="tc-divider" />

        <form onSubmit={handlePost} className="tc-form">
          <textarea
            className="tc-form__textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={isInternal ? 'Add an internal note (not visible to student)...' : 'Type your reply...'}
            rows={3}
          />
          <div className="tc-form__actions">
            {allowInternal && (
              <button
                type="button"
                className={`tc-form__toggle-btn${isInternal ? ' tc-form__toggle-btn--active' : ''}`}
                onClick={() => setIsInternal(!isInternal)}
              >
                <Lock size={14} />
                {isInternal ? 'Internal Note' : 'Public Reply'}
              </button>
            )}
            <button type="submit" className="tc-form__submit" disabled={posting || !text.trim()}>
              {posting ? (
                <Spinner size="sm" animation="border" />
              ) : (
                <>
                  <Send size={14} />
                  {isInternal ? 'Add Note' : 'Post Reply'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TicketComments;
